/**
 * HERMES Protocol — Vendor Analytics Dashboard
 * Tabs: Analytics (views/clicks/funnel) | Marina (restock + training) | Sage (BI + reports)
 * Route: /vendors/:vendorId/analytics
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft, BarChart3, Eye, MousePointerClick, ShoppingCart,
  TrendingUp, Package, MessageCircle, AlertTriangle, ClipboardList,
  Brain, FileText, Loader2, RefreshCw, ChevronDown, ChevronUp,
  ShieldCheck, Truck, BookOpen, Sparkles, Activity, Radio, Send, User, Gift
} from 'lucide-react';
import { BACKEND_URL, API } from '@/lib/backendUrl';


// ═══════════════════════════════════════════════════════════
// STAT CARD
// ═══════════════════════════════════════════════════════════

function StatCard({ label, value, sub, icon: Icon, color = 'text-[#F7931A]', testId }) {
  return (
    <Card className="bg-[#0a0f1a]/80 border-white/5" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
            <p className={`text-2xl font-bold mt-1 heading-font ${color}`}>{value}</p>
            {sub && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
          </div>
          {Icon && <div className="p-2 rounded-lg bg-white/5"><Icon className={`w-4 h-4 ${color}`} /></div>}
        </div>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════
// FUNNEL BAR
// ═══════════════════════════════════════════════════════════

function FunnelBar({ label, value, maxValue, pct, color }) {
  const width = maxValue > 0 ? Math.max((value / maxValue) * 100, 4) : 4;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-gray-400">{label}</span>
        <span className="text-white font-medium">{value.toLocaleString()} <span className="text-gray-500">({pct}%)</span></span>
      </div>
      <div className="h-3 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${width}%`, background: color }} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ANALYTICS TAB
// ═══════════════════════════════════════════════════════════

function AnalyticsTab({ vendorId }) {
  const [dashboard, setDashboard] = useState(null);
  const [products, setProducts] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [journeys, setJourneys] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/vendor/analytics/${vendorId}/dashboard`).then(r => r.json()),
      fetch(`${API}/api/vendor/analytics/${vendorId}/products`).then(r => r.json()),
      fetch(`${API}/api/vendor/analytics/${vendorId}/timeline`).then(r => r.json()),
      fetch(`${API}/api/vendor/analytics/${vendorId}/journeys`).then(r => r.json()),
    ]).then(([d, p, t, j]) => {
      setDashboard(d);
      setProducts(p);
      setTimeline(t);
      setJourneys(j);
    }).catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [vendorId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#F7931A]" /></div>;
  if (!dashboard) return <p className="text-gray-500 text-center py-8">No analytics data yet</p>;

  const o = dashboard.overview;
  const f = dashboard.conversion_funnel;
  const maxFunnel = Math.max(o.total_views, 1);

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Showroom Views" value={o.total_views.toLocaleString()} sub={`${o.unique_visitors} unique`} icon={Eye} testId="stat-views" />
        <StatCard label="Product Clicks" value={o.product_clicks.toLocaleString()} sub={`${f.view_to_click}% click rate`} icon={MousePointerClick} color="text-blue-400" testId="stat-clicks" />
        <StatCard label="Purchases" value={o.purchases.toLocaleString()} sub={`${f.overall_conversion}% conversion`} icon={ShoppingCart} color="text-emerald-400" testId="stat-purchases" />
        <StatCard label="Revenue" value={`$${(o.total_revenue || 0).toLocaleString()}`} sub={`${o.total_orders} orders`} icon={TrendingUp} color="text-purple-400" testId="stat-revenue" />
      </div>

      {/* Conversion Funnel */}
      <Card className="bg-[#0a0f1a]/80 border-white/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#F7931A]" /> Conversion Funnel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <FunnelBar label="Showroom Views" value={o.total_views} maxValue={maxFunnel} pct={100} color="#F7931A" />
          <FunnelBar label="Product Clicks" value={o.product_clicks} maxValue={maxFunnel} pct={f.view_to_click} color="#60A5FA" />
          <FunnelBar label="Buy Clicks" value={o.buy_clicks} maxValue={maxFunnel} pct={o.total_views > 0 ? ((o.buy_clicks / o.total_views) * 100).toFixed(1) : 0} color="#A855F7" />
          <FunnelBar label="Purchases" value={o.purchases} maxValue={maxFunnel} pct={f.overall_conversion} color="#34D399" />
        </CardContent>
      </Card>

      {/* Timeline Chart (simple bar) */}
      {timeline?.timeline?.length > 0 && (
        <Card className="bg-[#0a0f1a]/80 border-white/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#F7931A]" /> Daily Activity ({timeline.period_days}d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-28" data-testid="timeline-chart">
              {timeline.timeline.map((d, i) => {
                const total = d.views + d.clicks + d.purchases;
                const maxH = Math.max(...timeline.timeline.map(t => t.views + t.clicks + t.purchases), 1);
                const h = total > 0 ? Math.max((total / maxH) * 100, 8) : 4;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                    <div className="text-[7px] text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      {d.views}v {d.clicks}c {d.purchases}p
                    </div>
                    <div className="w-full rounded-t" style={{ height: `${h}%`, background: d.purchases > 0 ? '#34D399' : d.clicks > 0 ? '#60A5FA' : '#F7931A20' }} />
                    <span className="text-[7px] text-gray-600">{d.date.split(' ')[1]}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-center gap-4 mt-2 text-[9px] text-gray-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-[#F7931A]/20" /> Views Only</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-blue-400" /> Has Clicks</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-emerald-400" /> Has Purchases</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Product Heatmap */}
      {products?.products?.length > 0 && (
        <Card className="bg-[#0a0f1a]/80 border-white/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
              <Package className="w-4 h-4 text-[#F7931A]" /> Product Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {products.products.map((p, i) => (
                <div key={p.product_id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`product-row-${i}`}>
                  <div className="w-6 h-6 rounded bg-[#F7931A]/10 flex items-center justify-center text-[10px] font-bold text-[#F7931A]">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{p.name}</p>
                    <p className="text-[9px] text-gray-500">{p.category}</p>
                  </div>
                  <div className="text-right text-[10px]">
                    <p className="text-blue-400">{p.clicks} clicks</p>
                    <p className="text-emerald-400">{p.orders} orders</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold text-white">${(p.revenue || 0).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* QR Attribution Journeys */}
      {journeys && (journeys.attribution?.total_qr_scans > 0 || journeys.top_paths?.length > 0) && (
        <Card className="bg-[#0a0f1a]/80 border-white/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-purple-400" /> QR Attribution Journeys
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Attribution funnel */}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 rounded-lg bg-purple-500/5 border border-purple-500/10" data-testid="attr-qr">
                <p className="text-xl font-bold text-purple-400">{journeys.attribution.total_qr_scans}</p>
                <p className="text-[9px] text-gray-500">QR Scans</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-blue-500/5 border border-blue-500/10" data-testid="attr-showroom">
                <p className="text-xl font-bold text-blue-400">{journeys.attribution.qr_to_showroom}</p>
                <p className="text-[9px] text-gray-500">QR → Showroom ({journeys.attribution.qr_to_showroom_rate}%)</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10" data-testid="attr-purchase">
                <p className="text-xl font-bold text-emerald-400">{journeys.attribution.qr_to_purchase}</p>
                <p className="text-[9px] text-gray-500">QR → Purchase ({journeys.attribution.qr_to_purchase_rate}%)</p>
              </div>
            </div>

            {/* Top journey paths */}
            {journeys.top_paths?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Top Visitor Paths</p>
                {journeys.top_paths.map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`path-${i}`}>
                    <span className="text-[10px] text-gray-300 font-mono">{p.path}</span>
                    <Badge className="text-[8px] bg-purple-500/10 text-purple-300 border-purple-500/20">{p.count}x</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MARINA TAB (Restock + Product Training)
// ═══════════════════════════════════════════════════════════

function MarinaTab({ vendorId }) {
  const [alerts, setAlerts] = useState(null);
  const [orders, setOrders] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [briefingRoom, setBriefingRoom] = useState('');
  const [briefingHotel, setBriefingHotel] = useState('');
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [loadingBriefing, setLoadingBriefing] = useState(false);
  const [expandedAlert, setExpandedAlert] = useState(null);

  const checkRestock = useCallback((hotelId) => {
    if (!hotelId) { toast.error('Enter a hotel ID'); return; }
    setLoadingAlerts(true);
    fetch(`${API}/api/marina/vendor/restock-check`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hotel_id: hotelId }),
    }).then(r => r.json())
      .then(d => { setAlerts(d); toast.success(`Scanned ${d.rooms_scanned} rooms`); })
      .catch(() => toast.error('Restock check failed'))
      .finally(() => setLoadingAlerts(false));
  }, []);

  const generateBriefing = useCallback(() => {
    if (!briefingHotel || !briefingRoom) { toast.error('Enter hotel ID and room number'); return; }
    setLoadingBriefing(true);
    fetch(`${API}/api/marina/vendor/product-briefing`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hotel_id: briefingHotel, room_number: briefingRoom }),
    }).then(r => r.json())
      .then(d => { setBriefing(d); toast.success('Briefing generated'); })
      .catch(() => toast.error('Briefing failed'))
      .finally(() => setLoadingBriefing(false));
  }, [briefingHotel, briefingRoom]);

  const createOrder = useCallback((alert) => {
    fetch(`${API}/api/marina/vendor/replacement-order`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        hotel_id: alert.hotel_id || alerts?.hotel_id || '',
        room_number: alert.room_number,
        vendor_id: alert.vendor_id,
        product_id: alert.product_id,
        reason: 'lifecycle_expiry',
        priority: alert.severity === 'critical' ? 'urgent' : 'normal',
      }),
    }).then(r => r.json())
      .then(d => { if (d.success) toast.success(`Replacement order created: ${d.order.order_id}`); })
      .catch(() => toast.error('Order creation failed'));
  }, [alerts]);

  const severityColor = (s) => s === 'critical' ? 'text-red-400 border-red-500/20 bg-red-500/5' : s === 'warning' ? 'text-amber-400 border-amber-500/20 bg-amber-500/5' : 'text-blue-400 border-blue-500/20 bg-blue-500/5';

  return (
    <div className="space-y-6" data-testid="marina-tab">
      {/* Restock Intelligence */}
      <Card className="bg-[#0a0f1a]/80 border-white/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> Room Restock Intelligence
          </CardTitle>
          <p className="text-[10px] text-gray-500">Marina scans room products and alerts when replacements are needed</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <input
              type="text" placeholder="Hotel ID (e.g. hotel_grand_marina)"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-gray-600 focus:border-[#F7931A]/30 focus:outline-none"
              onChange={e => setBriefingHotel(e.target.value)}
              data-testid="restock-hotel-input"
            />
            <Button size="sm" className="bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 text-xs gap-1.5"
              onClick={() => checkRestock(briefingHotel)} disabled={loadingAlerts} data-testid="restock-check-btn">
              {loadingAlerts ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Scan Rooms
            </Button>
          </div>

          {alerts && (
            <div className="space-y-2">
              <div className="flex gap-2 text-[10px]">
                <Badge className="bg-red-500/10 text-red-400 border-red-500/20">{alerts.summary?.critical || 0} Critical</Badge>
                <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">{alerts.summary?.warning || 0} Warning</Badge>
                <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">{alerts.summary?.info || 0} Info</Badge>
              </div>
              {alerts.alerts?.length === 0 && (
                <p className="text-xs text-emerald-400 py-2">All products are within healthy lifecycle range</p>
              )}
              {alerts.alerts?.map((a, i) => (
                <div key={i} className={`p-2.5 rounded-lg border ${severityColor(a.severity)}`} data-testid={`alert-${i}`}>
                  <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedAlert(expandedAlert === i ? null : i)}>
                    <div>
                      <p className="text-xs font-medium">{a.product_name}</p>
                      <p className="text-[9px] opacity-70">Room {a.room_number} | {a.category} | {a.lifecycle_pct}% lifecycle</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="text-[8px] h-4" variant="outline">{a.severity}</Badge>
                      {expandedAlert === i ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </div>
                  </div>
                  {expandedAlert === i && (
                    <div className="mt-2 pt-2 border-t border-white/5 space-y-1.5 text-[10px]">
                      <p>Installed: {a.days_installed} days ago (max {a.lifecycle_days})</p>
                      <p>Condition: {a.condition_score}/100</p>
                      <p>{a.recommendation}</p>
                      <Button size="sm" className="mt-1 text-[10px] h-6 bg-[#F7931A]/10 text-[#F7931A] border border-[#F7931A]/20 hover:bg-[#F7931A]/20 gap-1"
                        onClick={() => createOrder(a)} data-testid={`order-btn-${i}`}>
                        <Truck className="w-3 h-3" /> Create Replacement Order
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Product Training Briefing */}
      <Card className="bg-[#0a0f1a]/80 border-white/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-purple-400" /> Staff Product Briefing
          </CardTitle>
          <p className="text-[10px] text-gray-500">Marina generates a briefing so staff can answer guest questions about room products</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <input
              type="text" placeholder="Hotel ID"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-gray-600 focus:border-purple-500/30 focus:outline-none"
              value={briefingHotel}
              onChange={e => setBriefingHotel(e.target.value)}
              data-testid="briefing-hotel-input"
            />
            <input
              type="text" placeholder="Room #"
              className="w-24 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-gray-600 focus:border-purple-500/30 focus:outline-none"
              value={briefingRoom}
              onChange={e => setBriefingRoom(e.target.value)}
              data-testid="briefing-room-input"
            />
            <Button size="sm" className="bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 text-xs gap-1.5"
              onClick={generateBriefing} disabled={loadingBriefing} data-testid="generate-briefing-btn">
              {loadingBriefing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
              Generate Briefing
            </Button>
          </div>

          {briefing && (
            <div className="p-3 rounded-lg bg-purple-500/5 border border-purple-500/10" data-testid="briefing-result">
              <div className="flex items-center gap-1.5 mb-2">
                <Sparkles className="w-3 h-3 text-purple-400" />
                <span className="text-[10px] text-purple-400 font-medium">AI-Generated by Marina</span>
              </div>
              <div className="text-xs text-gray-300 whitespace-pre-wrap leading-relaxed">{briefing.briefing}</div>
              {briefing.products?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-purple-500/10 flex flex-wrap gap-1">
                  {briefing.products.map((p, i) => (
                    <Badge key={i} className="text-[8px] bg-purple-500/10 text-purple-300 border-purple-500/20">
                      {p.name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SAGE TAB (BI + Performance Reports)
// ═══════════════════════════════════════════════════════════

function SageTab({ vendorId }) {
  const [intel, setIntel] = useState(null);
  const [report, setReport] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [experiments, setExperiments] = useState(null);
  const [abRecommendation, setAbRecommendation] = useState(null);
  const [loadingIntel, setLoadingIntel] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loadingAb, setLoadingAb] = useState(false);
  const [showCreateExp, setShowCreateExp] = useState(false);
  const [newExp, setNewExp] = useState({ name: '', controlDiscount: 10, variantDiscount: 15 });

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/sage/vendor/${vendorId}/intelligence`).then(r => r.json()),
      fetch(`${API}/api/sage/vendor/${vendorId}/predictions`).then(r => r.json()),
      fetch(`${API}/api/vendor/experiments/${vendorId}/results`).then(r => r.json()),
    ]).then(([i, p, e]) => { setIntel(i); setPredictions(p); setExperiments(e); })
      .catch(() => toast.error('Failed to load vendor intelligence'))
      .finally(() => setLoadingIntel(false));
  }, [vendorId]);

  const generateReport = useCallback(() => {
    setLoadingReport(true);
    fetch(`${API}/api/sage/vendor/${vendorId}/performance-report`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ focus: 'overall' }),
    }).then(r => r.json())
      .then(d => { setReport(d); toast.success('Performance report generated'); })
      .catch(() => toast.error('Report generation failed'))
      .finally(() => setLoadingReport(false));
  }, [vendorId]);

  if (loadingIntel) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#F7931A]" /></div>;

  return (
    <div className="space-y-6" data-testid="sage-tab">
      {/* BI Overview */}
      {intel && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Hotels Using" value={intel.deployment?.hotels_using || 0} icon={Package} color="text-cyan-400" testId="stat-hotels" />
            <StatCard label="Rooms Deployed" value={intel.deployment?.rooms_deployed || 0} icon={Eye} color="text-blue-400" testId="stat-rooms" />
            <StatCard label="Purchase Revenue" value={`$${(intel.revenue?.total_revenue || 0).toLocaleString()}`} sub={`${intel.revenue?.total_orders || 0} orders`} icon={ShoppingCart} color="text-emerald-400" testId="stat-bi-revenue" />
            <StatCard label="Per-Night Earnings" value={`$${(intel.per_night?.total_earnings || 0).toLocaleString()}`} sub={`${intel.per_night?.total_nights || 0} nights`} icon={TrendingUp} color="text-purple-400" testId="stat-per-night" />
          </div>

          {/* Category Deployment */}
          {Object.keys(intel.deployment?.category_breakdown || {}).length > 0 && (
            <Card className="bg-[#0a0f1a]/80 border-white/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-300 heading-font">Category Deployment</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(intel.deployment.category_breakdown).map(([cat, count]) => (
                    <Badge key={cat} className="bg-cyan-500/10 text-cyan-300 border-cyan-500/20 text-[10px] gap-1">
                      {cat} <span className="text-white font-bold">{count}</span>
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Product Intel Table */}
          {intel.products?.length > 0 && (
            <Card className="bg-[#0a0f1a]/80 border-white/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-cyan-400" /> Product Intelligence
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {intel.products.map((p, i) => (
                    <div key={p.product_id} className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`intel-product-${i}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-white truncate">{p.name}</p>
                        <p className="text-[9px] text-gray-500">{p.category} | ${p.unit_cost.toLocaleString()}</p>
                      </div>
                      <div className="text-center px-3">
                        <p className="text-xs font-bold text-cyan-400">{p.rooms_deployed}</p>
                        <p className="text-[8px] text-gray-600">rooms</p>
                      </div>
                      <div className="text-center px-3">
                        <p className="text-xs font-bold text-emerald-400">{p.orders}</p>
                        <p className="text-[8px] text-gray-600">orders</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-white">${(p.revenue || 0).toLocaleString()}</p>
                        <p className="text-[8px] text-gray-600">revenue</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Reorder Predictions */}
      {predictions?.predictions && (
        <Card className="bg-[#0a0f1a]/80 border-white/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-amber-400" /> Reorder Predictions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/10" data-testid="pred-30">
                <p className="text-xl font-bold text-red-400">{predictions.summary?.next_30 || 0}</p>
                <p className="text-[9px] text-gray-500">Next 30 Days</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/10" data-testid="pred-90">
                <p className="text-xl font-bold text-amber-400">{predictions.summary?.next_90 || 0}</p>
                <p className="text-[9px] text-gray-500">Next 90 Days</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10" data-testid="pred-180">
                <p className="text-xl font-bold text-blue-400">{predictions.summary?.next_180 || 0}</p>
                <p className="text-[9px] text-gray-500">Next 180 Days</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Performance Report */}
      <Card className="bg-[#0a0f1a]/80 border-white/5">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#F7931A]" /> AI Performance Report
            </CardTitle>
            <Button size="sm" className="bg-[#F7931A]/10 text-[#F7931A] border border-[#F7931A]/20 hover:bg-[#F7931A]/20 text-xs gap-1.5"
              onClick={generateReport} disabled={loadingReport} data-testid="generate-report-btn">
              {loadingReport ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Generate Report
            </Button>
          </div>
          <p className="text-[10px] text-gray-500">Sage AI analyzes your data and generates strategic insights</p>
        </CardHeader>
        {report && (
          <CardContent data-testid="sage-report">
            <div className="p-3 rounded-lg bg-[#F7931A]/5 border border-[#F7931A]/10">
              <div className="flex items-center gap-1.5 mb-2">
                <Sparkles className="w-3 h-3 text-[#F7931A]" />
                <span className="text-[10px] text-[#F7931A] font-medium">Generated by Sage AI</span>
                <span className="text-[8px] text-gray-600 ml-auto">{new Date(report.generated_at).toLocaleString()}</span>
              </div>
              <div className="text-xs text-gray-300 whitespace-pre-wrap leading-relaxed prose-sm">{report.report}</div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* A/B Pricing Experiments */}
      <Card className="bg-[#0a0f1a]/80 border-white/5">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-purple-400" /> A/B Pricing Experiments
            </CardTitle>
            <Button size="sm" className="bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 text-xs gap-1.5"
              onClick={() => setShowCreateExp(!showCreateExp)} data-testid="create-experiment-btn">
              {showCreateExp ? 'Cancel' : 'New Experiment'}
            </Button>
          </div>
          <p className="text-[10px] text-gray-500">Silently test pricing strategies — guests never see the experiment</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Create Experiment Form */}
          {showCreateExp && (
            <div className="p-3 rounded-lg bg-purple-500/5 border border-purple-500/10 space-y-2" data-testid="create-exp-form">
              <input type="text" placeholder="Experiment name (e.g., Stay discount A/B)"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-gray-600 focus:border-purple-500/30 focus:outline-none"
                value={newExp.name} onChange={e => setNewExp(p => ({ ...p, name: e.target.value }))}
                data-testid="exp-name-input" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] text-gray-500">Control (stay discount %)</label>
                  <input type="number" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none"
                    value={newExp.controlDiscount} onChange={e => setNewExp(p => ({ ...p, controlDiscount: +e.target.value }))}
                    data-testid="exp-control-input" />
                </div>
                <div>
                  <label className="text-[9px] text-gray-500">Variant (stay discount %)</label>
                  <input type="number" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none"
                    value={newExp.variantDiscount} onChange={e => setNewExp(p => ({ ...p, variantDiscount: +e.target.value }))}
                    data-testid="exp-variant-input" />
                </div>
              </div>
              <Button size="sm" className="w-full bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 text-xs"
                data-testid="submit-experiment-btn"
                onClick={() => {
                  if (!newExp.name) { toast.error('Name required'); return; }
                  fetch(`${API}/api/vendor/experiments/create`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      vendor_id: vendorId, name: newExp.name, duration_days: 14,
                      control: { stay_discount: newExp.controlDiscount },
                      variant: { stay_discount: newExp.variantDiscount },
                    }),
                  }).then(r => r.json()).then(d => {
                    if (d.success) { toast.success('Experiment created'); setShowCreateExp(false);
                      fetch(`${API}/api/vendor/experiments/${vendorId}/results`).then(r => r.json()).then(e => setExperiments(e));
                    }
                  }).catch(() => toast.error('Failed'));
                }}>
                Launch 14-Day Experiment
              </Button>
            </div>
          )}

          {/* Experiment Results */}
          {experiments?.experiments?.length > 0 ? (
            <div className="space-y-2">
              {experiments.experiments.map((exp) => {
                const isActive = exp.status === 'active';
                return (
                  <div key={exp.experiment_id} className={`p-3 rounded-lg border ${isActive ? 'bg-purple-500/5 border-purple-500/10' : 'bg-white/[0.02] border-white/5'}`}
                    data-testid={`experiment-${exp.experiment_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-white">{exp.name}</span>
                      <Badge className={`text-[7px] h-3 py-0 ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-gray-500/10 text-gray-400 border-gray-500/20'}`}>
                        {exp.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[10px]">
                      <div className="p-2 rounded bg-white/[0.02] border border-white/5">
                        <p className="text-gray-500 mb-0.5">Control</p>
                        <p className="text-white font-medium">{exp.control_metrics.conversion_rate}% conv</p>
                        <p className="text-gray-500">{exp.control_metrics.visitors} visitors</p>
                      </div>
                      <div className="p-2 rounded bg-white/[0.02] border border-white/5">
                        <p className="text-gray-500 mb-0.5">Variant</p>
                        <p className="text-white font-medium">{exp.variant_metrics.conversion_rate}% conv</p>
                        <p className="text-gray-500">{exp.variant_metrics.visitors} visitors</p>
                      </div>
                    </div>
                    {exp.winner !== 'inconclusive' && (
                      <p className="text-[9px] text-gray-400 mt-1.5">Result: <span className="text-purple-400 font-medium">{exp.winner}</span> · Confidence: {exp.confidence}</p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[10px] text-gray-600 text-center py-2">No experiments yet — create your first A/B test above</p>
          )}

          {/* AI Recommendation */}
          {experiments?.experiments?.length > 0 && (
            <div className="pt-2 border-t border-white/5">
              <Button size="sm" className="w-full bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 text-xs gap-1.5"
                onClick={() => {
                  setLoadingAb(true);
                  fetch(`${API}/api/vendor/experiments/${vendorId}/recommend`, { method: 'POST' })
                    .then(r => r.json()).then(d => setAbRecommendation(d.recommendation))
                    .catch(() => toast.error('Failed')).finally(() => setLoadingAb(false));
                }} disabled={loadingAb} data-testid="ab-recommend-btn">
                {loadingAb ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}
                Sage: Analyze Results
              </Button>
              {abRecommendation && (
                <div className="mt-2 p-2.5 rounded-lg bg-purple-500/5 border border-purple-500/10" data-testid="ab-recommendation">
                  <div className="flex items-center gap-1 mb-1">
                    <Sparkles className="w-3 h-3 text-purple-400" />
                    <span className="text-[9px] text-purple-400 font-medium">Sage Recommendation</span>
                  </div>
                  <p className="text-[10px] text-gray-300 leading-relaxed">{abRecommendation}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
// ═══════════════════════════════════════════════════════════

function LiveTab({ vendorId }) {
  const wsRef = useRef(null);
  const chatEndRef = useRef(null);
  const [visitors, setVisitors] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [bitsyNarration, setBitsyNarration] = useState([]);
  const [intentAlerts, setIntentAlerts] = useState([]);
  const [nudges, setNudges] = useState({});
  const [sendingOffer, setSendingOffer] = useState(null);
  const [offerDiscount, setOfferDiscount] = useState(10);

  useEffect(() => {
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/showroom/${vendorId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      ws.send(JSON.stringify({ action: 'join', visitor_id: `vendor_${vendorId}`, role: 'vendor', display_name: 'Vendor Owner' }));
    };
    ws.onclose = () => setConnected(false);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'init') {
          setVisitors(msg.data?.visitors || []);
        } else if (msg.type === 'presence') {
          const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          if (msg.action === 'join' && msg.visitor?.visitor_id?.startsWith('v_')) {
            setVisitors(prev => {
              const exists = prev.find(v => v.visitor_id === msg.visitor.visitor_id);
              return exists ? prev : [...prev, msg.visitor];
            });
            setBitsyNarration(prev => [...prev.slice(-20), {
              text: `${msg.visitor.display_name} entered the showroom${msg.visitor.from_qr ? ' via QR code' : ''}`,
              time: now, type: 'join',
            }]);
          } else if (msg.action === 'product_focus') {
            setVisitors(prev => prev.map(v =>
              v.visitor_id === msg.visitor_id ? { ...v, current_product: msg.product_name } : v
            ));
            setBitsyNarration(prev => [...prev.slice(-20), {
              text: `A guest is looking at "${msg.product_name}"`,
              time: now, type: 'focus',
            }]);
          } else if (msg.action === 'intent_update') {
            // Update visitor intent
            setVisitors(prev => prev.map(v =>
              v.visitor_id === msg.visitor_id ? { ...v, intent: msg.intent, current_product: msg.product_name } : v
            ));
            const lvl = msg.intent?.level || 'minimal';
            if (lvl === 'critical' || lvl === 'high') {
              const alertKey = `${msg.visitor_id}_${msg.product_id}`;
              setIntentAlerts(prev => {
                if (prev.find(a => a.key === alertKey)) return prev;
                return [...prev.slice(-10), {
                  key: alertKey,
                  visitor_id: msg.visitor_id,
                  product_name: msg.product_name,
                  product_id: msg.product_id,
                  score: msg.intent.score,
                  level: lvl,
                  label: msg.intent.label,
                  signals: msg.signals,
                  time: now,
                }];
              });
              setBitsyNarration(prev => [...prev.slice(-20), {
                text: `${lvl === 'critical' ? 'VERY HIGH' : 'HIGH'} intent detected on "${msg.product_name}" — Score: ${msg.intent.score}%`,
                time: now, type: 'intent',
              }]);
            }
          } else if (msg.action === 'leave') {
            setVisitors(prev => prev.filter(v => v.visitor_id !== msg.visitor_id));
            setBitsyNarration(prev => [...prev.slice(-20), {
              text: `A guest left the showroom`,
              time: now, type: 'leave',
            }]);
          }
        } else if (msg.type === 'chat') {
          setChatMessages(prev => [...prev.slice(-50), msg.data]);
        }
      } catch {}
    };

    const ping = setInterval(() => { if (ws.readyState === 1) ws.send('ping'); }, 25000);
    return () => { clearInterval(ping); ws.close(); };
  }, [vendorId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const sendChat = useCallback((targetVisitorId = '') => {
    if (!chatInput.trim() || wsRef.current?.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({
      action: 'chat', sender_id: `vendor_${vendorId}`, sender_name: 'Vendor', sender_role: 'vendor',
      text: chatInput.trim(), visitor_id: targetVisitorId,
    }));
    setChatInput('');
  }, [chatInput, vendorId]);

  const requestNudge = useCallback((visitorId) => {
    fetch(`${API}/api/vendor/intent/analyze`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vendor_id: vendorId, visitor_id: visitorId }),
    }).then(r => r.json())
      .then(d => { if (d.nudge) setNudges(prev => ({ ...prev, [visitorId]: d.nudge })); })
      .catch(() => toast.error('Nudge analysis failed'));
  }, [vendorId]);

  const sendOffer = useCallback((alert) => {
    fetch(`${API}/api/vendor/intent/offer`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        vendor_id: vendorId, visitor_id: alert.visitor_id,
        product_id: alert.product_id, product_name: alert.product_name,
        discount_pct: offerDiscount,
      }),
    }).then(r => r.json())
      .then(d => { if (d.success) { toast.success(`${offerDiscount}% offer sent!`); setSendingOffer(null); } })
      .catch(() => toast.error('Failed to send offer'));
  }, [vendorId, offerDiscount]);

  const guestVisitors = visitors.filter(v => !v.visitor_id?.startsWith('vendor_'));

  const intentColor = (lvl) => {
    if (lvl === 'critical') return 'text-red-400 bg-red-500/10 border-red-500/20';
    if (lvl === 'high') return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
    if (lvl === 'medium') return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
    return 'text-gray-500 bg-white/5 border-white/10';
  };

  return (
    <div className="space-y-6" data-testid="live-tab">
      {/* Connection Status */}
      <div className="flex items-center gap-3">
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-medium border ${
          connected ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
        }`} data-testid="ws-status">
          <Radio className={`w-3 h-3 ${connected ? 'animate-pulse' : ''}`} />
          {connected ? 'Live — Watching Showroom' : 'Disconnected'}
        </div>
        <Badge className="bg-white/5 border-white/10 text-gray-400 text-[10px]" data-testid="visitor-count">
          {guestVisitors.length} visitor{guestVisitors.length !== 1 ? 's' : ''} now
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Visitors + Bitsy Narration */}
        <div className="space-y-4">
          {/* Live Visitors */}
          <Card className="bg-[#0a0f1a]/80 border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
                <User className="w-4 h-4 text-emerald-400" /> Live Visitors
              </CardTitle>
            </CardHeader>
            <CardContent>
              {guestVisitors.length === 0 ? (
                <p className="text-xs text-gray-600 text-center py-4">No visitors in the showroom right now</p>
              ) : (
                <div className="space-y-2">
                  {guestVisitors.map((v) => (
                    <div key={v.visitor_id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`visitor-${v.visitor_id}`}>
                      <div className="w-7 h-7 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                        <User className="w-3.5 h-3.5 text-emerald-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-medium text-white">{v.display_name}</p>
                          {v.intent?.score > 0 && (
                            <Badge className={`text-[7px] h-3 py-0 border ${intentColor(v.intent.level)}`} data-testid={`intent-badge-${v.visitor_id}`}>
                              {v.intent.score}% {v.intent.level}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-[9px] text-gray-500">
                          {v.from_qr && <Badge className="text-[7px] h-3 bg-purple-500/10 text-purple-400 border-purple-500/20 py-0">QR</Badge>}
                          {v.room && <span>Room {v.room}</span>}
                          {v.current_product && (
                            <span className="text-[#F7931A]">Looking at: {v.current_product}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => requestNudge(v.visitor_id)}
                        className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-[#F7931A] transition-colors"
                        title="Get AI nudge"
                        data-testid={`nudge-btn-${v.visitor_id}`}
                      >
                        <Brain className="w-3.5 h-3.5" />
                      </button>
                      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bitsy Narration */}
          <Card className="bg-[#0a0f1a]/80 border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#F7931A]" /> Bitsy Narration
              </CardTitle>
              <p className="text-[10px] text-gray-500">Bitsy narrates what visitors are doing in real-time</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="narration-feed">
                {bitsyNarration.length === 0 ? (
                  <p className="text-[10px] text-gray-600 text-center py-3">Waiting for visitor activity...</p>
                ) : (
                  bitsyNarration.map((n, i) => (
                    <div key={i} className={`flex items-start gap-2 p-1.5 rounded text-[10px] ${
                      n.type === 'join' ? 'text-emerald-400' :
                      n.type === 'focus' ? 'text-[#F7931A]' :
                      n.type === 'intent' ? 'text-red-400 font-medium bg-red-500/5' :
                      'text-gray-500'
                    }`}>
                      <span className="text-gray-600 shrink-0">{n.time}</span>
                      <span>{n.text}</span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* AI Nudge Cards */}
          {Object.entries(nudges).length > 0 && (
            <Card className="bg-[#0a0f1a]/80 border-[#F7931A]/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#F7931A]" /> Bitsy AI Nudges
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(nudges).map(([vid, nudge]) => (
                  <div key={vid} className="p-2.5 rounded-lg bg-[#F7931A]/5 border border-[#F7931A]/10" data-testid={`nudge-card-${vid}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <Badge className={`text-[7px] h-3 py-0 border ${intentColor(nudge.intent_level)}`}>
                        {nudge.intent_score}% — {nudge.product_name}
                      </Badge>
                      {nudge.suggested_discount > 0 && (
                        <Badge className="text-[7px] h-3 py-0 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                          Suggest {nudge.suggested_discount}% off
                        </Badge>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-300 leading-relaxed">{nudge.text}</p>
                    <Button
                      size="sm"
                      className="mt-2 h-5 text-[9px] bg-[#F7931A]/10 text-[#F7931A] border border-[#F7931A]/20 hover:bg-[#F7931A]/20 gap-1"
                      onClick={() => setSendingOffer({ visitor_id: vid, product_id: nudge.product_id, product_name: nudge.product_name, suggested: nudge.suggested_discount })}
                      data-testid={`send-offer-btn-${vid}`}
                    >
                      <Send className="w-2.5 h-2.5" /> Send Offer
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Intent Alert Cards */}
          {intentAlerts.length > 0 && (
            <Card className="bg-[#0a0f1a]/80 border-red-500/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" /> Intent Alerts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {intentAlerts.slice(-5).reverse().map((a, i) => (
                  <div key={a.key} className={`p-2 rounded-lg border ${intentColor(a.level)}`} data-testid={`intent-alert-${i}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[10px] font-bold">{a.score}% — {a.product_name}</span>
                        <p className="text-[8px] opacity-70">{a.label} | {a.time}</p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => requestNudge(a.visitor_id)}
                          className="p-1 rounded hover:bg-white/10 text-[#F7931A]"
                          title="Get AI recommendation"
                        >
                          <Brain className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => setSendingOffer({ visitor_id: a.visitor_id, product_id: a.product_id, product_name: a.product_name, suggested: a.score >= 80 ? 5 : 10 })}
                          className="p-1 rounded hover:bg-white/10 text-emerald-400"
                          title="Send offer"
                        >
                          <Send className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Live Chat + Send Offer Modal */}
        <div className="space-y-4">
          {/* Send Offer Inline */}
          {sendingOffer && (
            <Card className="bg-[#0a0f1a]/80 border-[#F7931A]/20 animate-in slide-in-from-top" data-testid="send-offer-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#F7931A] heading-font flex items-center gap-2">
                  <Gift className="w-4 h-4" /> Send Discount Offer
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-[10px] text-gray-400">Product: <span className="text-white">{sendingOffer.product_name}</span></p>
                <div className="flex items-center gap-3">
                  <label className="text-[10px] text-gray-500">Discount:</label>
                  <div className="flex items-center gap-1.5">
                    {[5, 10, 15, 20].map(pct => (
                      <button key={pct}
                        onClick={() => setOfferDiscount(pct)}
                        className={`px-2.5 py-1 rounded text-[10px] font-bold border transition-all ${
                          offerDiscount === pct
                            ? 'bg-[#F7931A]/20 border-[#F7931A]/40 text-[#F7931A]'
                            : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                        }`}
                        data-testid={`discount-${pct}`}
                      >{pct}%</button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1 bg-[#F7931A] text-black font-bold hover:bg-[#F7931A]/90 gap-1 text-xs"
                    onClick={() => sendOffer(sendingOffer)} data-testid="confirm-offer-btn">
                    <Send className="w-3 h-3" /> Send {offerDiscount}% Offer
                  </Button>
                  <Button size="sm" variant="outline" className="border-white/10 text-gray-400 text-xs"
                    onClick={() => setSendingOffer(null)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="bg-[#0a0f1a]/80 border-white/5 flex flex-col">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-300 heading-font flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-blue-400" /> Live Chat with Visitors
              </CardTitle>
              <p className="text-[10px] text-gray-500">Message guests browsing your showroom in real-time</p>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              {/* Messages */}
              <div className="flex-1 min-h-[250px] max-h-[350px] overflow-y-auto space-y-1.5 mb-3 p-2 rounded-lg bg-white/[0.01] border border-white/5" data-testid="chat-messages">
                {chatMessages.length === 0 ? (
                  <p className="text-[10px] text-gray-600 text-center py-8">No messages yet. Say hello to your visitors!</p>
                ) : (
                  chatMessages.map((m, i) => (
                    <div key={i} className={`flex ${m.sender_role === 'vendor' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] p-2 rounded-lg text-[10px] ${
                        m.sender_role === 'vendor'
                          ? 'bg-[#F7931A]/10 border border-[#F7931A]/20 text-[#F7931A]'
                          : 'bg-white/5 border border-white/10 text-gray-300'
                      }`}>
                        <span className="font-bold">{m.sender_name}</span>
                        <p className="mt-0.5">{m.text}</p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Type a message to visitors..."
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendChat()}
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-gray-600 focus:border-[#F7931A]/30 focus:outline-none"
                  data-testid="vendor-chat-input"
                />
                <Button size="sm" className="bg-[#F7931A]/10 text-[#F7931A] border border-[#F7931A]/20 hover:bg-[#F7931A]/20 gap-1"
                  onClick={() => sendChat()} data-testid="vendor-chat-send">
                  <Send className="w-3 h-3" /> Send
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════

const TABS = [
  { id: 'live', label: 'Live', icon: Radio, color: 'text-emerald-400' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, color: 'text-[#F7931A]' },
  { id: 'marina', label: 'Marina', icon: ShieldCheck, color: 'text-amber-400' },
  { id: 'sage', label: 'Sage', icon: Brain, color: 'text-cyan-400' },
];

export default function VendorAnalyticsPage() {
  const { vendorId } = useParams();
  const [tab, setTab] = useState('analytics');
  const [vendor, setVendor] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/vendor/${vendorId}`).then(r => r.json())
      .then(d => setVendor(d.vendor))
      .catch(() => {});
  }, [vendorId]);

  return (
    <div className="min-h-screen bg-[#030712] text-white" data-testid="vendor-analytics-page">
      {/* Header */}
      <div className="border-b border-white/5 bg-[#0a0f1a]/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to={`/vendors/${vendorId}/showroom`} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 text-gray-400" />
            </Link>
            <div>
              <h1 className="text-sm font-bold heading-font">{vendor?.company_name || 'Vendor Analytics'}</h1>
              <p className="text-[10px] text-gray-500">Vendor Intelligence Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {vendor?.categories?.map(cat => (
              <Badge key={cat} variant="outline" className="text-[7px] h-3.5 border-purple-500/20 text-purple-400 py-0">{cat}</Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-6xl mx-auto px-4 pt-4">
        <div className="flex gap-1 bg-white/[0.02] rounded-lg p-1 border border-white/5 w-fit">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-medium transition-all ${
                tab === t.id
                  ? 'bg-white/10 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className={`w-3.5 h-3.5 ${tab === t.id ? t.color : ''}`} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-6xl mx-auto px-4 py-5">
        {tab === 'live' && <LiveTab vendorId={vendorId} />}
        {tab === 'analytics' && <AnalyticsTab vendorId={vendorId} />}
        {tab === 'marina' && <MarinaTab vendorId={vendorId} />}
        {tab === 'sage' && <SageTab vendorId={vendorId} />}
      </div>
    </div>
  );
}
